import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import Pagination from "../Pagination";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import ReactMarkdown from "react-markdown";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import apiClient from "../../api/apiClient";

function TasksList() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState("");
  const [language, setLanguage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tasks, setTasks] = useState({ results: [] });
  const { id } = useSelector((state) => state.auth.user);

  const languageOptions = [
    { display: "All", value: "" },
    { display: "Python", value: "python" },
    { display: "JavaScript", value: "javascript" },
    { display: "Dart", value: "dart" },
    { display: "PHP", value: "php" },
    { display: "Ruby", value: "ruby" },
    { display: "Rust", value: "rust" },
    { display: "Kotlin", value: "kotlin" },
    { display: "Scala", value: "scala" },
    { display: "Erlang", value: "erlang" },
    { display: "Elixir", value: "elixir" },
    { display: "Java", value: "java" },
    { display: "C#", value: "csharp" },
    { display: "Swift", value: "swift" },
    { display: "Go", value: "go" },
    { display: "C++", value: "c%2B%2B" },
    { display: "C", value: "c" },
    { display: "TypeScript", value: "typescript" },
  ];

  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  const getTasks = async () => {
    try {
      const params = {
        page: currentPage,
        ...(language && { language }),
      };
      const response = await apiClient.get("/api/tasks/tasks/", { params });
      setTasks(response.data);
      setCurrentPage(response.data.current_page);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
    }
  };

  useEffect(() => {
    getTasks();
  }, [language, currentPage]);

  const handleViewClick = (prompt) => {
    setSelectedPrompt(prompt);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPrompt("");
  };

  const navigate = useNavigate();
  const handleEnrollClick = async (taskId) => {
    try {
      await apiClient.post(`/api/tasks/select-task/${taskId}/`);
      navigate(`/tasks/${taskId}`);
    } catch (error) {
      if (error.response && error.response.status === 400) {
        Swal.fire({
          icon: "error",
          title: "Enrollment Failed",
          text: error.response.data.error || "Bad request",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Something went wrong! Please try again.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    }
  };

  // Helper function to get language badge color
  const getLanguageBadgeColor = (language) => {
    const colorMap = {
      python: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
      javascript: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
      java: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
      csharp: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
      swift: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
      go: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300",
      "c%2B%2B": "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300",
      c: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
      typescript: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
    };

    return colorMap[language] || "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300";
  };

  return (
    <div className="w-full">
      {/* Filter Section */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex items-center">
            <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300">
              Filter by Language
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setLanguage("")}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 
              ${language === "" 
                ? "bg-gradient-to-r from-cyan-500 to-emerald-500 text-white shadow-md" 
                : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"}`}
            >
              All
            </button>
            {languageOptions.filter(option => option.value !== "").map((lang) => (
              <button
                key={lang.value}
                onClick={() => setLanguage(lang.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 
                ${language === lang.value 
                  ? "bg-gradient-to-r from-cyan-500 to-emerald-500 text-white shadow-md" 
                  : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"}`}
              >
                {lang.display}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tasks Cards */}
      <div className="grid grid-cols-1 gap-4">
        {Array.isArray(tasks.results) && tasks.results.length > 0 ? (
          tasks.results.map((task) => {
            const langDisplay = languageOptions.find(option => option.value === task.language)?.display || task.language;
            const isClaimedByUser = task.claimed_by === id;
            return (
              <div 
                key={task.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow duration-200"
              >
                <div className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300">
                        Task #{task.id}
                      </span>
                      <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${getLanguageBadgeColor(task.language)}`}>
                        {langDisplay}
                      </span>
                      {isClaimedByUser && (
                        <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300">
                          In Progress
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {isClaimedByUser ? (
                        <NavLink
                          to={`/tasks/${task.id}`}
                          className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-md bg-gradient-to-r from-cyan-500 to-emerald-500 text-white hover:from-cyan-600 hover:to-emerald-600 transition-all duration-200"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                          Continue
                        </NavLink>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => handleViewClick(task.prompt)}
                            className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-all duration-200"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                            </svg>
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEnrollClick(task.id)}
                            className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-md bg-gradient-to-r from-cyan-500 to-emerald-500 text-white hover:from-cyan-600 hover:to-emerald-600 transition-all duration-200"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                            </svg>
                            Enroll
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3">
                      {task.prompt}
                    </p>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-8">
            <div className="flex flex-col items-center justify-center text-center">
              <svg className="w-16 h-16 text-gray-400 dark:text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2z"></path>
              </svg>
              <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-1">No tasks found</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Try adjusting your filter or wait for new tasks to be assigned</p>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-6 flex justify-center">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>

      {/* Prompt View Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-lg shadow-xl animate-fadeIn">
            <div className="flex items-start justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Task Prompt
              </h3>
              <button
                type="button"
                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white"
                onClick={closeModal}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
                <span className="sr-only">Close modal</span>
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-96 overflow-y-auto dark:text-gray-300">
              <ReactMarkdown
                components={{
                  code({ node, inline, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || "");
                    return !inline && match ? (
                      <div className="overflow-x-auto rounded-md">
                        <SyntaxHighlighter
                          style={atomDark}
                          showLineNumbers={true}
                          language={match[1]}
                          PreTag="div"
                          {...props}
                        >
                          {String(children).replace(/\n$/, "")}
                        </SyntaxHighlighter>
                      </div>
                    ) : (
                      <code className={`${className} px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700`} {...props}>
                        {children}
                      </code>
                    );
                  },
                }}
              >
                {selectedPrompt}
              </ReactMarkdown>
            </div>
            
            <div className="flex items-center justify-end p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-gradient-to-r from-cyan-500 to-emerald-500 text-white hover:from-cyan-600 hover:to-emerald-600 transition-all duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TasksList;